"use client";

import React from "react";
import { Scale, Activity, Wrench, Zap, Briefcase, Building2, Hammer, ShoppingBag, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectorItems = [{
  id: "accountants",
  title: "Accountants & Financial Services",
  description: "Attract high-value corporate clients and local wealth management leads with tailored, trust-focused search engine optimization.",
  iconName: "Briefcase",
  highlight: "B2B Lead Generation"
}, {
  id: "dentists",
  title: "Dentists & Healthcare Clinics",
  description: "Dominate local search results for high-ticket dental treatments including Invisalign, implants, and cosmetic procedures.",
  iconName: "Activity",
  highlight: "Local Map Pack & High Value"
}, {
  id: "plumbers",
  title: "Plumbers & Heating Engineers",
  description: "Capture urgent emergency callouts and planned boiler installation searches across your specific local coverage areas.",
  iconName: "Wrench",
  highlight: "Service Area Dominance"
}, {
  id: "electricians",
  title: "Electricians & Contractors",
  description: "Build organic visibility for EV charger installations, commercial rewires, and specialized electrical services.",
  iconName: "Zap",
  highlight: "High-Intent Inquiries"
}, {
  id: "solicitors",
  title: "Solicitors & Law Firms",
  description: "Position your legal practice for competitive keywords in conveyancing, family law, personal injury, and corporate law.",
  iconName: "Scale",
  highlight: "Competitive Legal Search"
}, {
  id: "estate-agents",
  title: "Estate Agents & Property Services",
  description: "Drive valuation inquiries and landlord instructions organically by targeting hyper-local property search terms.",
  iconName: "Building2",
  highlight: "Valuation & Landlord Leads"
}, {
  id: "construction",
  title: "Builders & Construction Companies",
  description: "Win lucrative commercial contracts and residential development projects through authoritative technical SEO.",
  iconName: "Hammer",
  highlight: "High-Ticket Projects"
}, {
  id: "ecommerce",
  title: "Ecommerce & Retail Brands",
  description: "Scale organic revenue with specialized collection optimization, technical site architecture, and AI search presence.",
  iconName: "ShoppingBag",
  highlight: "Revenue-Focused Organic"
}];
export default function Sectors() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "Briefcase":
        return <Briefcase className="size-6 text-primary" />;
      case "Activity":
        return <Activity className="size-6 text-primary" />;
      case "Wrench":
        return <Wrench className="size-6 text-primary" />;
      case "Zap":
        return <Zap className="size-6 text-primary" />;
      case "Scale":
        return <Scale className="size-6 text-primary" />;
      case "Building2":
        return <Building2 className="size-6 text-primary" />;
      case "Hammer":
        return <Hammer className="size-6 text-primary" />;
      case "ShoppingBag":
        return <ShoppingBag className="size-6 text-primary" />;
      default:
        return <Sparkles className="size-6 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="industrysectorsgrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border/50">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center text-center mb-16 max-w-3xl mx-auto">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary/80">
            Tailored Sector Expertise
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default mb-4">
            Industry Sectors I Specialist In
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every industry has unique search behaviors, keyword intents, and competitive dynamics. 
            I engineer bespoke SEO strategies engineered specifically for your market vertical.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {sectorItems.map((sector, i) => <motion.div key={sector.id} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full flex flex-col justify-between border-border/60 bg-card hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="pt-6 pb-2">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                      {getIcon(sector.iconName)}
                    </div>
                    <Badge variant="outline" className="text-[12.5px] font-normal border-border/80 text-muted-foreground">
                      {sector.highlight}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg font-semibold text-foreground leading-snug">
                    {sector.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="py-2 flex-grow">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {sector.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-4 pb-6">
                  <div className="flex items-center text-xs font-medium text-primary gap-1.5">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>Direct consultant access</span>
                  </div>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 bg-muted/50 rounded-2xl p-8 md:p-10 border border-border/60 text-center max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left max-w-xl">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Don't see your specific industry listed?
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              I apply proven, search engine fundamentals and customized keyword research tailored to any competitive niche or commercial industry.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/ContactPage" className="inline-flex items-center gap-2">
              Book a Free SEO Consultation
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}